import React, { useState } from 'react';
import { Tooltip, IconButton, Box } from '@mui/material';
import LogoutIcon from "@mui/icons-material/Logout";
import { EXITIMPERSONATE_FAIL, EXITIMPERSONATE_SUCCESSFUL } from '../../../constants/messages';
import { AuthTokenDto, EndImpersonationRequestDto, Impersonator } from '../../../types/User';
import { GLOBALSETTINGS, IMPERSONATION_INFO, LOCALSTORAGENAME, SEVERITIES } from '../../../data/constants';
import useApi from '../../../hooks/useApi';
import { endImpersonateUser } from '../../../services/userService';
import { endImpersonation, setOrRevokeImpersonation } from '../../../utils/commonHelper';
import { useToaster } from '../../../hooks/useToaster';
import { useUser } from '../../../context/UserContext';
import { useDispatch } from 'react-redux';
import { stopImpersonation } from '../../../redux/impersonationSlice';
import BackdropProgressBar from '../../../components/ui/BackdropProgressBar';

const SIGNIN_ROUTE = "/signin";

const ImpersonationBanner: React.FC = () => {
    const { executeApi } = useApi();
    const showToaster = useToaster();
    const { setUserId, setUserName, setFirstName, setLastName } = useUser();
    const dispatch = useDispatch();
    // Drives the full-screen loading overlay shown while exiting impersonation and
    // restoring the original session (the API call + reload below). It is intentionally
    // NOT cleared on the success path so the overlay stays up until the reload completes.
    const [isExiting, setIsExiting] = useState(false);

    const handleStopImpersonation = async () => {
        if (isExiting) return;
        setIsExiting(true);

        const storedImpersonator = localStorage.getItem(IMPERSONATION_INFO.Impersonator);
        const isStillImpersonating =
            localStorage.getItem(IMPERSONATION_INFO.IsImpersonating) === "true";

        // The impersonator info is removed when the session times out / logs out
        // (localStorage is cleared). If it's gone the original session can no longer be
        // restored, so clear any residual impersonation state and route to sign-in instead
        // of dereferencing null and surfacing an error.
        if (!storedImpersonator || !isStillImpersonating) {
            endImpersonation();
            dispatch(stopImpersonation());
            window.location.href = SIGNIN_ROUTE;
            return;
        }

        let targetUserName = "";
        try {
            const originalImpersonator: Impersonator = JSON.parse(storedImpersonator);
            targetUserName = originalImpersonator.targetUserName;
            const impersonationId = localStorage.getItem(IMPERSONATION_INFO.ImpersonationId)
                ? Number(localStorage.getItem(IMPERSONATION_INFO.ImpersonationId))
                : null;
            const endImpersonationRequestDto: EndImpersonationRequestDto = {
                impersonationId: impersonationId,
                impersonatorUserId: originalImpersonator.impersonatorUserId
            };
            const response = await executeApi<EndImpersonationRequestDto, boolean>(endImpersonateUser, endImpersonationRequestDto);
            if (response) {
                const authToken: AuthTokenDto = {
                    accessToken: originalImpersonator.impersonatorToken,
                    refreshToken: originalImpersonator.impersonatorRefreshToken
                }
                setOrRevokeImpersonation(null, null, authToken, originalImpersonator.impersonatorSessionValue,
                    originalImpersonator.impersonatorUserId, originalImpersonator.impersonatorUserName, true,
                    setUserId, setUserName, {
                        firstName: originalImpersonator.impersonatorFirstName,
                        lastName: originalImpersonator.impersonatorLastName,
                        phone: originalImpersonator.impersonatorPhone,
                        email: originalImpersonator.impersonatorEmail,
                        setFirstName,
                        setLastName,
                    });
                dispatch(stopImpersonation());
                // Stash the success message so it survives the reload below, then do a full
                // reload to DEFAULT_PAGE. A reload is required because ending impersonation can
                // happen while already on DEFAULT_PAGE (e.g. the Files/Folders page), where a
                // router navigate() is a no-op and leaves the folder tree/list, breadcrumbs and
                // other caches showing the impersonated user's data. Reloading re-fetches every
                // data source under the original admin's token and session.
                localStorage.setItem(LOCALSTORAGENAME.PENDINGTOAST, EXITIMPERSONATE_SUCCESSFUL(originalImpersonator.targetUserName));
                window.location.href = GLOBALSETTINGS.DEFAULT_PAGE;
            }
            else {
                showToaster(EXITIMPERSONATE_FAIL(targetUserName), SEVERITIES.ERROR);
                setIsExiting(false);
            }
        }
        catch {
            showToaster(EXITIMPERSONATE_FAIL(targetUserName), SEVERITIES.ERROR);
            setIsExiting(false);
        }
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: "8px" }}>
            <BackdropProgressBar loading={isExiting} />
            <Tooltip title="End Impersonation">
                <IconButton
                    id="endImpersonation"
                    size="medium"
                    disabled={isExiting}
                    onClick={handleStopImpersonation}
                    sx={{
                        backgroundColor: 'white',
                        color: 'orange',
                        '&:hover': {
                            backgroundColor: '#f0f0f0',
                        },
                    }}
                >
                    <LogoutIcon fontSize="medium" />
                </IconButton>
            </Tooltip>
        </Box >

    )
};

export default ImpersonationBanner;
